/**
 * Variant Thumbnail Filter
 * Filters product thumbnails based on selected variant's alt text
 */

class VariantThumbnailFilter {
  constructor() {
    this.debug = false; // Set to true for debugging
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      this.filterThumbnails(event.detail);
    });

    // Also listen for form changes as a fallback
    document.addEventListener('change', (event) => {
      if (event.target.closest('variant-selects') || 
          event.target.closest('.product-form__input') ||
          event.target.name === 'id') {
        // Small delay to ensure variant data is updated
        setTimeout(() => this.filterThumbnails(), 100);
      }
    });

    // Initial filter on page load
    this.filterThumbnails();
  }

  getSelectedVariantTitle() {
    // Try to get variant title from various sources
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const selectedInputs = variantSelects.querySelectorAll('input[type="radio"]:checked, select option:checked');
      if (selectedInputs.length > 0) {
        // Get the selected option values
        const selectedValues = Array.from(selectedInputs).map(input => {
          if (input.tagName === 'SELECT') {
            return input.selectedOptions[0]?.value || '';
          }
          return input.value || '';
        }).filter(value => value);
        
        // Return the first non-empty value (usually the main variant option like color)
        return selectedValues[0] || '';
      }
    }

    // Fallback: try to get from variant ID input
    const variantIdInput = document.querySelector('input[name="id"]');
    if (variantIdInput) {
      const selectedOption = document.querySelector(`option[value="${variantIdInput.value}"]`);
      if (selectedOption) {
        return selectedOption.textContent.trim();
      }
    }

    return '';
  }

  filterThumbnails(variantData = null) {
    const mediaGalleries = document.querySelectorAll('media-gallery');
    
    mediaGalleries.forEach(gallery => {
      const thumbnailsContainer = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (!thumbnailsContainer) return;

      const thumbnails = thumbnailsContainer.querySelectorAll('.thumbnail-list__item');
      if (thumbnails.length === 0) return;

      // Get the selected variant title
      let variantTitle = '';
      if (variantData && variantData.variant) {
        // Extract the main option (usually color) from variant title
        variantTitle = this.extractMainVariantOption(variantData.variant.title);
      } else {
        variantTitle = this.getSelectedVariantTitle();
      }

      // If no variant title found, show all thumbnails
      if (!variantTitle) {
        this.showAllThumbnails(thumbnails);
        return;
      }

      // Filter thumbnails based on variant title
      let visibleCount = 0;
      thumbnails.forEach(thumbnail => {
        const variantFilter = thumbnail.getAttribute('data-variant-filter');
        const shouldShow = !variantFilter || 
                          this.matchesVariant(variantFilter, variantTitle);

        if (this.debug) {
          console.log('Thumbnail filter:', {
            variantFilter,
            variantTitle,
            shouldShow
          });
        }

        if (shouldShow) {
          // Use CSS classes for smooth transitions instead of direct style manipulation
          thumbnail.style.display = '';
          thumbnail.classList.remove('hidden');
          visibleCount++;
        } else {
          // Add hidden class for smooth transition, then hide completely
          thumbnail.classList.add('hidden');
          setTimeout(() => {
            if (thumbnail.classList.contains('hidden')) {
              thumbnail.style.display = 'none';
            }
          }, 300); // Match CSS transition duration
        }
      });

      // Update slider if it exists
      this.updateSlider(thumbnailsContainer, visibleCount);
    });
  }

  matchesVariant(filterText, variantTitle) {
    if (!filterText || !variantTitle) return false;
    
    const filterLower = filterText.toLowerCase().trim();
    const variantLower = variantTitle.toLowerCase().trim();
    
    // Direct match
    if (filterLower === variantLower) return true;
    
    // Contains match (either direction)
    if (filterLower.includes(variantLower) || variantLower.includes(filterLower)) return true;
    
    // Split by common separators and check each part
    const filterParts = filterLower.split(/[\s,/-]+/);
    const variantParts = variantLower.split(/[\s,/-]+/);
    
    return filterParts.some(part => 
      part.length > 1 && variantParts.some(vPart => 
        vPart.includes(part) || part.includes(vPart)
      )
    );
  }

  extractMainVariantOption(variantTitle) {
    // Extract the main option (usually the first part before comma or slash)
    if (!variantTitle) return '';
    
    // Split by common separators and take the first part
    const parts = variantTitle.split(/[,/]/);
    return parts[0]?.trim() || variantTitle.trim();
  }

  showAllThumbnails(thumbnails) {
    thumbnails.forEach(thumbnail => {
      thumbnail.style.display = '';
      thumbnail.classList.remove('hidden');
    });
  }

  updateSlider(container, visibleCount) {
    // Update slider visibility and controls based on visible thumbnails
    const slider = container.querySelector('.slider');
    if (!slider) return;

    // Update slider buttons visibility
    const prevButton = container.querySelector('.slider-button--prev');
    const nextButton = container.querySelector('.slider-button--next');
    
    if (prevButton && nextButton) {
      const shouldHideButtons = visibleCount <= 3;
      prevButton.style.display = shouldHideButtons ? 'none' : '';
      nextButton.style.display = shouldHideButtons ? 'none' : '';
    }

    // Update slider class for styling
    if (visibleCount <= 3) {
      slider.classList.add('thumbnail-slider--no-slide');
    } else {
      slider.classList.remove('thumbnail-slider--no-slide');
    }
  }
}

// Initialize the filter
new VariantThumbnailFilter();
